import { useTheme } from "@emotion/react";
import {
  Cancel,
  CheckCircle,
  Clear,
  ExpandMore,
  ManageAccountsOutlined,
  Search,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import api from "../../../../../../services/api";
import DatagridComponent from "../../../../../../components/datagridComponent";
import ErpChip from "../../../../../../components/erp/ErpChip";
import { temErp } from "../../../../../../services/erpSistema";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import { irPara } from "../../buscaConfiguracao";
import { EVENTO_ABERTURA_SALVA } from "../configOpenTicketCliente";
import useStyles from "./styles";

// Mesma regra de createTicketCliente (backend): sem estes campos o portal
// recusa a abertura do ticket.
const aberturaCompleta = (p) =>
  Boolean(
    p?.origemContatoId &&
      p?.tipoAtendimentoId &&
      p?.statusAtendimentoId &&
      p?.usuariosResponsaveis?.length &&
      p?.idEtapaFluxoAtendimento,
  );

export default function ConfigSuporte() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Tudo aqui é do portal/tickets da HubSoft (no IXC o portal não abre ticket
  // nem troca mensagens). São regras, não IDs do ERP: valem para os clientes
  // de todos os ERPs HubSoft.
  const temHubsoft = temErp("HUBSOFT");
  const [configClienteGlobal, setConfigClienteGlobal] = useState({
    openTicket: false,
    finishTicket: false,
    sendMessage: false,
    sendAnexo: false,
  });

  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [duplicateTicketWindow, setDuplicateTicketWindow] = useState({
    ativo: false,
    valor: 1,
    unidade: "horas",
  });
  const [suporteGeneralSettings, setSuporteGeneralSettings] = useState({});
  const [configCliente, setConfigCliente] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscarCliente, setBuscarCliente] = useState("");
  const [snackbar, setSnackbar] = useState(null);
  // Só clientes HubSoft (sem ERP: os antigos, da variável ERP).
  const filteredRows = configCliente
    ?.filter((row) => !row.erp || row.erp.tipo === "HUBSOFT")
    ?.filter((row, index) => {
      row.numero = index;

      return (
        row.nome
          ?.toLowerCase()
          ?.includes(buscarCliente?.toLowerCase()?.trim())
      );
    });

  const openTicketLiberadoGlobal = configClienteGlobal?.openTicket;
  const sendMessageLiberadoGlobal = configClienteGlobal?.sendMessage;
  const sendAnexoLiberadoGlobal = configClienteGlobal?.sendAnexo;

  const columnsCliente = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "cliente",
      headerName: "cliente",
      flex: 1,
      renderCell: (params) => {
        return params.row.nome || "";
      },
    },
    {
      field: "erp",
      headerName: "ERP",
      width: 110,
      renderCell: (params) => <ErpChip erp={params?.row?.erp} />,
    },
    !openTicketLiberadoGlobal && {
      field: "openTicket",
      headerName: "Permitir abrir ticket",
      width: 200,
      renderCell: (params) => (
        <Switch
          disabled={loadingUpdate}
          size="small"
          checked={params.row.openTicket}
          onChange={(e) => {
            updateConfigCliente(params?.row?.id, e.target.checked);
          }}
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
        />
      ),
    },
    !sendMessageLiberadoGlobal && {
      field: "sendMessage",
      headerName: "Permitir enviar mensagem",
      width: 200,
      renderCell: (params) => (
        <Switch
          disabled={loadingUpdate}
          size="small"
          checked={params.row.sendMessage}
          onChange={(e) => {
            updateConfigCliente(params?.row?.id, null, e.target.checked);
          }}
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
        />
      ),
    },
    !sendAnexoLiberadoGlobal && {
      field: "sendAnexo",
      headerName: "Permitir anexar arquivo",
      width: 200,
      renderCell: (params) => (
        <Switch
          disabled={loadingUpdate}
          size="small"
          checked={params.row.sendAnexo}
          onChange={(e) => {
            updateConfigCliente(params?.row?.id, null, null, e.target.checked);
          }}
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
        />
      ),
    },
  ].filter(Boolean);

  // Busca só depois da primeira abertura do accordion.
  const [jaAberto, setJaAberto] = useState(false);
  useEffect(() => {
    if (!jaAberto) return;
    getConfig();
  }, [jaAberto]);

  // Liberar a abertura aqui não basta: o portal também precisa da
  // configuração de como o ticket é criado, que é por ERP e fica em
  // "Configurações de abertura de ticket por clientes". Confere cada HubSoft
  // (só banco local) para avisar quando a liberação ficaria sem efeito.
  const { erps, carregado } = useErpSelecionado(["HUBSOFT"]);
  const [aberturaPorErp, setAberturaPorErp] = useState({}); // { erpId: completa }
  const erpsHubsoft = erps?.length ? erps : [{ id: "padrao", nome: "HubSoft" }];
  const verificarAbertura = async () => {
    const resultado = await Promise.all(
      erpsHubsoft.map(async (erp) => {
        try {
          const response = await api.post("/interno/find/configTicket", {
            erpId: erp.id === "padrao" ? undefined : erp.id,
            tipoConfig: 1,
          });
          const configs = Array.isArray(response?.data) ? response.data : [];
          return [
            erp.id,
            configs.some((c) =>
              aberturaCompleta(c?.config?.paramsOpenTicketCliente),
            ),
          ];
        } catch (error) {
          return [erp.id, true]; // sem como conferir: não avisa
        }
      }),
    );
    setAberturaPorErp(Object.fromEntries(resultado));
  };
  useEffect(() => {
    if (!jaAberto || !carregado) return;
    verificarAbertura();
    window.addEventListener(EVENTO_ABERTURA_SALVA, verificarAbertura);
    return () =>
      window.removeEventListener(EVENTO_ABERTURA_SALVA, verificarAbertura);
  }, [jaAberto, carregado]);

  // HubSofts com clientes liberados (todos, se a liberação é global; cliente
  // sem ERP é do HubSoft em uso, o primeiro) e sem configuração completa.
  const erpsLiberados = openTicketLiberadoGlobal
    ? erpsHubsoft.map((erp) => erp.id)
    : configCliente
        .filter((row) => row.openTicket && (!row.erp || row.erp.tipo === "HUBSOFT"))
        .map((row) => row.erp?.id || erpsHubsoft[0].id);
  const erpsSemAbertura = erpsHubsoft.filter(
    (erp) => erpsLiberados.includes(erp.id) && aberturaPorErp[erp.id] === false,
  );

  const getConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get("/interno/find/configClientes");

      setConfigClienteGlobal(response?.data?.configGlobal);
      setConfigCliente(response?.data?.configClientes);

      const resGeneral = await api.post("/interno/find/generalSetting", {
        product: "suporte",
      });
      const fetchedSettings = resGeneral?.data || {};
      setSuporteGeneralSettings(fetchedSettings);
      const windowSetting =
        fetchedSettings?.duplicateTicketWindow ||
        fetchedSettings?.settings?.duplicateTicketWindow;
      if (windowSetting) {
        setDuplicateTicketWindow({
          ativo: windowSetting.ativo === true,
          valor: windowSetting.valor || 1,
          unidade: windowSetting.unidade || "horas",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar configuração:", error);
      setSnackbar({
        children: "Erro ao buscar configuração!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDuplicateTicketWindow = async (newVal) => {
    setLoadingUpdate(true);
    try {
      const updatedSettings = {
        ...suporteGeneralSettings,
        duplicateTicketWindow: newVal,
      };
      await api.post("/interno/upsert/generalSettings", {
        product: "suporte",
        settings: updatedSettings,
      });
      setSuporteGeneralSettings(updatedSettings);
      setSnackbar({
        children: "Configuração de alerta de duplicidade salva!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao salvar configuração de duplicidade:", error);
      setSnackbar({
        children: "Erro ao salvar configuração!",
        severity: "error",
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const updateConfigGlobal = async (
    id,
    openTicket,
    config,
    type,
    sendMessage = undefined,
    sendAnexo = undefined,
  ) => {
    //type 2 = liberação global (cliente), 3 = timeline cliente
    setLoadingUpdate(true);

    try {
      const response = await api.post("/interno/update/configClienteGlobal", {
        idConfigGlobal: id,
        openTicket: openTicket,
        config: config || null,
        sendMessage: sendMessage,
        sendAnexo: sendAnexo,
      });
      setConfigClienteGlobal((prev) => {
        return {
          ...prev,
          openTicket:
            response?.data?.openTicket !== undefined
              ? response?.data?.openTicket
              : prev?.openTicket,
          sendMessage:
            response?.data?.sendMessage !== undefined
              ? response?.data?.sendMessage
              : prev?.sendMessage,
          sendAnexo:
            response?.data?.sendAnexo !== undefined
              ? response?.data?.sendAnexo
              : prev?.sendAnexo,
          config:
            response?.data?.config !== undefined
              ? response?.data?.config
              : prev?.config,
        };
      });

      setSnackbar({
        children: "Configuração global de cliente atualizada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao atualizar configuração:", error);
      setSnackbar({
        children: "Erro ao atualizar configuração global!",
        severity: "error",
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const updateConfigCliente = async (
    clienteId,
    openTicket,
    sendMessage,
    sendAnexo,
  ) => {
    setLoadingUpdate(true);
    try {
      const response = await api.post("/interno/update/configCliente", {
        clienteId: clienteId,
        openTicket: openTicket,
        sendMessage: sendMessage,
        sendAnexo: sendAnexo,
      });

      const updatedConfig = configCliente.map((config) =>
        config.id === response?.data?.clienteId
          ? {
              ...config,
              openTicket: response?.data?.openTicket,
              sendMessage: response?.data?.sendMessage,
              sendAnexo: response?.data?.sendAnexo,
            }
          : config,
      );

      setConfigCliente(updatedConfig);

      setSnackbar({
        children: "Configuração de cliente atualizada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao atualizar configuração do cliente:", error);
      setSnackbar({
        children: "Erro ao atualizar configuração do cliente!",
        severity: "error",
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  return (
    <>
      <Accordion data-secao="cliente" onChange={(_, aberto) => aberto && setJaAberto(true)}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ManageAccountsOutlined color="primary" />
              <Typography
                variant="h5"
                sx={{
                  ...styles.title,
                }}
              >
                Configurações de cliente
              </Typography>
              <ErpChip
                erp={{
                  tipo: "HUBSOFT",
                  nome: "Somente HubSoft — vale para os clientes de todos os ERPs HubSoft",
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {temHubsoft && erpsSemAbertura.length > 0 && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        sx={{ whiteSpace: "nowrap" }}
                        onClick={() => irPara({ secao: "abertura-cliente" })}
                      >
                        Configurar abertura
                      </Button>
                    }
                  >
                    <AlertTitle>Os clientes ainda não conseguem abrir tickets</AlertTitle>
                    A abertura está liberada, mas falta definir como o ticket é
                    criado (tipo, status, origem, responsáveis e etapa)
                    {erps?.length > 1
                      ? ` para ${erpsSemAbertura.map((erp) => erp.nome).join(", ")}`
                      : ""}
                    .
                  </Alert>
                )}
                {temHubsoft && (
                  <Paper variant="outlined" sx={styles.paperContainer}>
                    <Box sx={styles.boxContainer}>
                      <Box sx={styles.switchesGroup}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={loadingUpdate}
                                  sx={styles.switch}
                                  checked={openTicketLiberadoGlobal}
                                  onChange={(e) => {
                                    setConfigClienteGlobal((prev) => ({
                                      ...prev,
                                      openTicket: e.target.checked,
                                    }));
                                    updateConfigGlobal(
                                      configClienteGlobal?.id,
                                      e.target.checked,
                                      null,
                                      2,
                                    );
                                  }}
                                  checkedIcon={<CheckCircle sx={styles.icon} />}
                                  icon={<Cancel sx={styles.icon} />}
                                  focusVisibleClassName=".Mui-focusVisible"
                                  disableRipple
                                />
                              }
                              sx={{ margin: 0 }}
                            />
                          </FormGroup>

                          <Box>
                            <Typography data-busca="cliente-abrir" variant="body2" sx={styles.titleSwitch}>
                              Liberar abertura de ticket para todos os clientes.
                            </Typography>
                            <Typography sx={styles.subtitle}>
                              Quando ativado, todos os clientes terão permissão
                              para abrir tickets no sistema.
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={loadingUpdate}
                                  sx={styles.switch}
                                  checked={sendMessageLiberadoGlobal}
                                  onChange={(e) => {
                                    setConfigClienteGlobal((prev) => ({
                                      ...prev,
                                      sendMessage: e.target.checked,
                                    }));
                                    updateConfigGlobal(
                                      configClienteGlobal?.id,
                                      null,
                                      null,
                                      2,
                                      e.target.checked,
                                    );
                                  }}
                                  checkedIcon={<CheckCircle sx={styles.icon} />}
                                  icon={<Cancel sx={styles.icon} />}
                                  focusVisibleClassName=".Mui-focusVisible"
                                  disableRipple
                                />
                              }
                              sx={{ margin: 0 }}
                            />
                          </FormGroup>

                          <Box>
                            <Typography data-busca="cliente-mensagem" variant="body2" sx={styles.titleSwitch}>
                              Liberar envio de mensagem dentro do ticket para
                              todos os clientes.
                            </Typography>
                            <Typography sx={styles.subtitle}>
                              Quando ativado, todos os clientes poderão enviar
                              mensagens dentro dos tickets no sistema.
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={loadingUpdate}
                                  sx={styles.switch}
                                  checked={sendAnexoLiberadoGlobal}
                                  onChange={(e) => {
                                    setConfigClienteGlobal((prev) => ({
                                      ...prev,
                                      sendAnexo: e.target.checked,
                                    }));
                                    updateConfigGlobal(
                                      configClienteGlobal?.id,
                                      null,
                                      null,
                                      2,
                                      undefined,
                                      e.target.checked,
                                    );
                                  }}
                                  checkedIcon={<CheckCircle sx={styles.icon} />}
                                  icon={<Cancel sx={styles.icon} />}
                                  focusVisibleClassName=".Mui-focusVisible"
                                  disableRipple
                                />
                              }
                              sx={{ margin: 0 }}
                            />
                          </FormGroup>

                          <Box>
                            <Typography data-busca="cliente-anexo" variant="body2" sx={styles.titleSwitch}>
                              Liberar anexo de arquivos dentro do ticket para
                              todos os clientes.
                            </Typography>
                            <Typography sx={styles.subtitle}>
                              Quando ativado, todos os clientes poderão anexar
                              arquivos dentro dos tickets no sistema.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {!(
                        openTicketLiberadoGlobal &&
                        sendMessageLiberadoGlobal &&
                        sendAnexoLiberadoGlobal
                      ) && (
                        <TextField
                          label="Buscar cliente"
                          data-busca="cliente-permissoes"
                          variant="outlined"
                          autoComplete="off"
                          placeholder="Cliente"
                          value={buscarCliente}
                          size="small"
                          onChange={(event) => {
                            setBuscarCliente(event?.target?.value?.trimStart());
                          }}
                          sx={styles.searchField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search />
                              </InputAdornment>
                            ),
                            endAdornment: buscarCliente ? (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setBuscarCliente("")}
                                  edge="end"
                                >
                                  <Clear size={"small"} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      )}
                    </Box>
                    <Collapse
                      in={
                        !(
                          openTicketLiberadoGlobal &&
                          sendMessageLiberadoGlobal &&
                          sendAnexoLiberadoGlobal
                        )
                      }
                    >
                      {" "}
                      <Paper
                        variant="outlined"
                        elevation={3}
                        sx={styles.paperDatagrid}
                      >
                        <DatagridComponent
                          columns={columnsCliente}
                          rows={filteredRows}
                        />
                      </Paper>
                    </Collapse>
                  </Paper>
                )}
                {temHubsoft && (
                  <Paper variant="outlined" sx={styles.paperContainer}>
                    <Box sx={styles.boxContainer}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                disabled={loadingUpdate}
                                sx={styles.switch}
                                checked={
                                  configClienteGlobal?.config
                                    ?.timelineCliente || false
                                }
                                onChange={(e) => {
                                  setConfigClienteGlobal((prev) => ({
                                    ...prev,
                                    config: {
                                      ...prev?.config,
                                      timelineCliente: e.target.checked,
                                    },
                                  }));
                                  updateConfigGlobal(
                                    configClienteGlobal?.id,
                                    null,
                                    {
                                      ...configClienteGlobal?.config,
                                      timelineCliente: e.target.checked,
                                    },
                                    3,
                                  );
                                }}
                                checkedIcon={<CheckCircle sx={styles.icon} />}
                                icon={<Cancel sx={styles.icon} />}
                                focusVisibleClassName=".Mui-focusVisible"
                                disableRipple
                              />
                            }
                            sx={{ margin: 0 }}
                          />
                        </FormGroup>
                        <Box>
                          <Typography data-busca="cliente-linha-tempo" variant="body2" sx={styles.titleSwitch}>
                            Exibir linha do tempo para o cliente
                          </Typography>
                          <Typography sx={styles.subtitle}>
                            Quando ativado, todos os clientes terão acesso à aba
                            'Linha do tempo' nos detalhes do ticket.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                )}
                {temHubsoft && (
                  <Paper variant="outlined" sx={styles.paperContainer}>
                    <Box sx={styles.boxContainer}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={loadingUpdate}
                                  sx={styles.switch}
                                  checked={duplicateTicketWindow.ativo === true}
                                  onChange={(e) => {
                                    const nextVal = {
                                      ...duplicateTicketWindow,
                                      ativo: e.target.checked,
                                    };
                                    setDuplicateTicketWindow(nextVal);
                                    updateDuplicateTicketWindow(nextVal);
                                  }}
                                  checkedIcon={<CheckCircle sx={styles.icon} />}
                                  icon={<Cancel sx={styles.icon} />}
                                  focusVisibleClassName=".Mui-focusVisible"
                                  disableRipple
                                />
                              }
                              sx={{ margin: 0 }}
                            />
                          </FormGroup>
                          <Box>
                            <Typography data-busca="cliente-duplicado" variant="body2" sx={styles.titleSwitch}>
                              Alerta de ticket duplicado por unidade
                            </Typography>
                            <Typography sx={styles.subtitle}>
                              Janela de tempo em que o sistema avisa se já
                              existe atendimento aberto para o mesmo serviço e
                              tipo.
                            </Typography>
                          </Box>
                        </Box>
                        {duplicateTicketWindow.ativo === true && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <TextField
                              label="Tempo"
                              type="text"
                              inputMode="numeric"
                              size="small"
                              sx={{ width: 90 }}
                              value={duplicateTicketWindow.valor}
                              disabled={loadingUpdate}
                              onChange={(e) => {
                                const rawValue = e.target.value.replace(
                                  /\D/g,
                                  "",
                                );
                                const val = Math.max(1, Number(rawValue));
                                setDuplicateTicketWindow((prev) => ({
                                  ...prev,
                                  valor: val,
                                }));
                              }}
                            />
                            <Select
                              size="small"
                              value={duplicateTicketWindow.unidade}
                              disabled={loadingUpdate}
                              onChange={(e) => {
                                setDuplicateTicketWindow((prev) => ({
                                  ...prev,
                                  unidade: e.target.value,
                                }));
                              }}
                            >
                              <MenuItem value="minutos">Minutos</MenuItem>
                              <MenuItem value="horas">Horas</MenuItem>
                              <MenuItem value="dias">Dias</MenuItem>
                              <MenuItem value="semanas">Semanas</MenuItem>
                              <MenuItem value="meses">Meses</MenuItem>
                            </Select>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={loadingUpdate}
                              onClick={() =>
                                updateDuplicateTicketWindow(
                                  duplicateTicketWindow,
                                )
                              }
                            >
                              Salvar
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}
          </Paper>
        </AccordionDetails>
      </Accordion>
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
